import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Lottie from 'lottie-react';
import './GameImageCard.css';

const GameImageCard = ({
  image,
  animationData = null,
  alt = 'Game',
  onClick,
  style = {},
  disabled = false,
}) => {
  const isDisabled = !!disabled;
  const lottieRef = useRef(null);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event) => setReduceMotion(event.matches);
    setReduceMotion(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const restart = () => {
    if (isDisabled || reduceMotion) return;
    lottieRef.current?.goToAndPlay(0, true);
  };

  return (
    <button
      type="button"
      className="game-image-card"
      aria-label={alt}
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      onMouseEnter={(e) => {
        if (!isDisabled) e.currentTarget.style.transform = 'scale(1.05)';
        restart();
      }}
      onFocus={restart}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
      }}
      style={{
        position: 'relative',
        display: 'block',
        width: '100%',
        height: 'auto',
        border: 'none',
        background: 'transparent',
        padding: 0,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        overflow: 'visible',
        opacity: isDisabled ? 0.7 : 1,
        pointerEvents: isDisabled ? 'none' : 'auto',
        transform: 'scale(1)',
        transition: 'transform 0.3s ease',
        ...style,
      }}
    >
      {animationData ? (
        <Lottie
          lottieRef={lottieRef}
          className="game-image-card__lottie"
          style={
            animationData.w && animationData.h
              ? { aspectRatio: `${animationData.w} / ${animationData.h}` }
              : undefined
          }
          animationData={animationData}
          loop={!reduceMotion && !isDisabled}
          autoplay={!reduceMotion && !isDisabled}
          renderer="svg"
          rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
        />
      ) : (
        <img
          src={image}
          alt={alt}
          style={{
            display: 'block',
            width: '100%',
            height: 'auto',
            objectFit: 'contain',
            pointerEvents: 'none',
            filter: 'drop-shadow(0px 4px 4px rgba(0,0,0,0.25))',
          }}
        />
      )}
    </button>
  );
};

GameImageCard.propTypes = {
  image: PropTypes.string.isRequired,
  animationData: PropTypes.object,
  alt: PropTypes.string,
  onClick: PropTypes.func,
  style: PropTypes.object,
  disabled: PropTypes.bool,
};

export default GameImageCard;
